import { useCallback, useEffect } from 'react';
import { useURLFilterState } from '@client/hooks/useURLFilterState';
import { useActiveSubstanceEnrichedData } from './useActiveSubstanceEnrichedData';
import { ACTIVE_SUBSTANCE_FIELD_TO_ENRICHED } from '@client/config/filterFieldMappings';

/**
 * Filter keys configuration for active substance search
 * @type {string[]}
 */
const ACTIVE_SUBSTANCE_FILTERS = [
  // CAS number was merged into the `names` filter — the substance name
  // dropdown now matches CAS numbers and synonyms via its search_fields,
  // and displays "name (cas1, cas2)" next to each option.
  'names',
  'cultures',
  'activity_sectors',
  'cultures_practices',
  'chemical_families',
  'pesticide_groups',
  'pesticide_categories',
  'pesticide_types',
  'activity_types',
  'arla_culture_groups',
  'resistance',
  'acute_toxicity',
  'long_term_effects',
  'fish_or_daphnia',
  'birds',
  'bees',
  'persistence',
  'leaching_potential',
];

/**
 * Specific hook for active substance filtering with URL synchronization
 * Composes useURLFilterState with enriched data for cold start support
 * @returns {Object} Filter state and methods for active substance search
 */
export const useActiveSubstanceFilters = () => {
  const urlState = useURLFilterState(ACTIVE_SUBSTANCE_FILTERS, {
    defaultPagination: { page: 1, size: 20 },
    throttleMs: 300,
    clearOnDefault: true,
  });

  const {
    enrichedData,
    isLoadingLabels,
    resetEnrichedData,
    updateEnrichedField,
    getLabelsForField,
    buildSelectionLabels,
  } = useActiveSubstanceEnrichedData(urlState.filters);

  // Sync enriched labels into the filter labels store for display
  useEffect(() => {
    if (isLoadingLabels || !enrichedData) return;

    Object.entries(ACTIVE_SUBSTANCE_FIELD_TO_ENRICHED).forEach(([filterKey, enrichedKey]) => {
      const items = enrichedData[enrichedKey];
      if (items && items.length > 0) {
        items.forEach((item) => {
          if (item.value && item.label) {
            urlState.setFilterLabel(filterKey, item.value, item.label);
          }
        });
      }
    });
  }, [enrichedData, isLoadingLabels, urlState.setFilterLabel]);

  // Wrap resetFilters to also reset enriched data
  const resetFilters = useCallback(() => {
    urlState.resetFilters();
    resetEnrichedData();
  }, [urlState.resetFilters, resetEnrichedData]);

  return {
    ...urlState,
    resetFilters,
    enrichedData,
    isLoadingLabels,
    updateEnrichedField,
    getLabelsForField,
    buildSelectionLabels,
  };
};
